import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Alert, Badge, Button, Card, ChevronRight, Clock, EmptyState, Metric, PageHeader, ProgressBar, Select, Table } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import StaffingTabs, { STAFFING_SUPPORTING_TEXT, staffingTabPathsShape } from "./components/StaffingTabs";
import PayrollExportModal from "./components/PayrollExportModal";
import TimeCardDrawer from "./components/TimeCardDrawer";
import { errorMessages } from "./components/staffingFormatters";
import { hoursLabel, moneyLabel } from "./components/scheduleFormatters";
import {
  FLAG_BADGES,
  PERIOD_BADGES,
  STATUS_BADGES,
  cardDateLabel,
  hoursOrDash,
  periodLabel,
  rangeLabel,
  takenBreakMinutes,
} from "./components/timesheetFormatters";

const ALL_VENUES = "";

const textStyle = (size, color, weight = 400) => ({ fontSize: size, lineHeight: `${size + 6}px`, color: `var(--sl-${color})`, fontWeight: weight });

const PERSON_COLUMNS = "minmax(200px, 2fr) repeat(7, minmax(72px, 1fr)) minmax(120px, 1.2fr) minmax(140px, 1.4fr) 96px";

const sum = (cards, key) => cards.reduce((total, card) => total + (card[key] || 0), 0);

export const personTotals = (person) => ({
  scheduled: person.scheduledMinutes,
  actual: sum(person.cards, "workedMinutes"),
  regular: sum(person.cards, "regularMinutes"),
  overtime: sum(person.cards, "overtimeMinutes"),
  doubleTime: sum(person.cards, "doubleTimeMinutes"),
  cost: sum(person.cards, "cost"),
  withBreak: person.cards.filter((card) => takenBreakMinutes(card.breaks) > 0).length,
  flags: person.cards.reduce((total, card) => total + card.flags.length, 0),
  approved: person.cards.filter((card) => card.status === "approved").length,
});

const Flags = ({ flags }) => (
  <div className="d-flex flex-wrap" style={{ gap: 4 }}>
    {flags.map((flag) => (
      <Badge key={flag} size="sm" color={FLAG_BADGES[flag]?.color || "gray"}>
        {FLAG_BADGES[flag]?.label || flag}
      </Badge>
    ))}
  </div>
);

Flags.propTypes = { flags: PropTypes.arrayOf(PropTypes.string).isRequired };

const StatusBadge = ({ status, locked }) => {
  const badge = locked && status !== "approved" ? { label: "Locked", color: "gray" } : STATUS_BADGES[status];
  return (
    <Badge size="sm" color={badge.color}>
      {badge.label}
    </Badge>
  );
};

StatusBadge.propTypes = { status: PropTypes.string.isRequired, locked: PropTypes.bool.isRequired };

const PersonRow = ({ person, expanded, onToggle, showCost, locked, busy, onApprove, onEdit }) => {
  const totals = personTotals(person);
  const allApproved = totals.approved === person.cards.length;
  const approvable = person.cards.filter((card) => card.canApprove);
  const statusLabel = allApproved ? "Approved" : locked ? "Locked" : `${totals.approved} of ${person.cards.length} approved`;
  const cardColumns = [
    { key: "date", header: "Date · event", primary: true, render: (card) => <CardDate card={card} /> },
    { key: "scheduled", header: "Scheduled", render: (card) => (card.shift ? rangeLabel(card.shift.startsAt, card.shift.endsAt) : "Unscheduled") },
    { key: "actual", header: "Actual", render: (card) => rangeLabel(card.clockInAt, card.clockOutAt) },
    { key: "break", header: "Break", render: (card) => (takenBreakMinutes(card.breaks) ? `${takenBreakMinutes(card.breaks)}m` : "—") },
    { key: "hours", header: "Hours", align: "right", render: (card) => hoursLabel(card.workedMinutes) },
    { key: "flags", header: "Flags", render: (card) => <Flags flags={card.flags} /> },
    { key: "status", header: "Status", render: (card) => <StatusBadge status={card.status} locked={locked} /> },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (card) => (
        <div className="d-flex justify-content-end" style={{ gap: 4 }}>
          {card.canEdit && (
            <Button size="xs" variant="tertiary" onClick={() => onEdit(card)}>
              Edit
            </Button>
          )}
          {card.canApprove && (
            <Button size="xs" variant="secondary" disabled={busy} onClick={() => onApprove([card])}>
              Approve
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ borderTop: "1px solid var(--sl-border-secondary)", background: expanded ? "var(--sl-bg-secondary)" : "transparent" }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && onToggle()}
        className="align-items-center"
        style={{ display: "grid", gridTemplateColumns: PERSON_COLUMNS, gap: 8, padding: "12px 16px", cursor: "pointer", ...textStyle(14, "text-secondary") }}
      >
        <div className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
          <ChevronRight width={16} height={16} style={{ transform: expanded ? "rotate(90deg)" : "none", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={textStyle(14, "text-primary", 600)}>{person.name}</div>
            <div style={textStyle(12, "text-tertiary")}>
              {person.positionNames.join(", ")} · {person.cards.length} {person.cards.length === 1 ? "card" : "cards"}
            </div>
          </div>
        </div>
        <span>{hoursOrDash(totals.scheduled)}</span>
        <span>{hoursLabel(totals.actual)}</span>
        <span>{hoursLabel(totals.regular)}</span>
        <span style={totals.overtime ? textStyle(14, "text-warning", 600) : undefined}>{hoursOrDash(totals.overtime)}</span>
        <span>{hoursOrDash(totals.doubleTime)}</span>
        <span>
          {totals.withBreak} / {person.cards.length}
        </span>
        <span>{showCost ? moneyLabel(totals.cost) : ""}</span>
        <span>{totals.flags > 0 && <Badge size="sm" color="warning">{`${totals.flags} ${totals.flags === 1 ? "flag" : "flags"}`}</Badge>}</span>
        <span>
          <Badge size="sm" color={allApproved ? "success" : "gray"}>
            {statusLabel}
          </Badge>
        </span>
        <span className="d-flex justify-content-end">
          {approvable.length > 0 && (
            <Button
              size="xs"
              variant="secondary"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                onApprove(approvable, person.name);
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              Approve
            </Button>
          )}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: "0 16px 16px 40px" }}>
          <Table columns={cardColumns} data={person.cards} getRowKey={(card) => card.id} stackBelow={720} />
        </div>
      )}
    </div>
  );
};

const CardDate = ({ card }) => (
  <div>
    <div style={textStyle(14, "text-primary", 600)}>{cardDateLabel(card.clockInAt)}</div>
    <div style={textStyle(12, "text-tertiary")}>{card.eventTitle || card.venueName}</div>
  </div>
);

CardDate.propTypes = { card: PropTypes.object.isRequired };

PersonRow.propTypes = {
  person: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showCost: PropTypes.bool.isRequired,
  locked: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

const StaffingTimesheetsApp = ({ tabPaths, timesheet: initialTimesheet, staffChoices, paths }) => {
  const [timesheet, setTimesheet] = useState(initialTimesheet);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const locked = timesheet.payPeriod.status !== "open";
  const cards = timesheet.people.flatMap((person) => person.cards);
  const approvedCount = cards.filter((card) => card.status === "approved").length;
  const needsReview = cards.filter((card) => card.flags.length > 0 && card.status !== "approved").length;
  const clean = cards.filter((card) => card.canApprove && card.flags.length === 0);
  const totals = timesheet.people.map(personTotals);
  const total = (key) => totals.reduce((sumSoFar, personTotal) => sumSoFar + personTotal[key], 0);

  const filters = useRef({ pay_period_id: initialTimesheet.payPeriod.id, venue_ownership_id: initialTimesheet.venueOwnershipId ?? ALL_VENUES });
  const latestRequest = useRef(0);

  const load = (overrides = {}) => {
    filters.current = { ...filters.current, ...overrides };
    const requestId = ++latestRequest.current;
    return client.get(paths.timesheet, { params: filters.current }).then(({ data }) => {
      if (requestId === latestRequest.current) setTimesheet(data);
    });
  };

  const run = (request, success) => {
    setBusy(true);
    return request()
      .then(success)
      .then(() => load())
      .catch((error) => toastError(errorMessages(error)[0] || "Something went wrong. Try again."))
      .finally(() => setBusy(false));
  };

  const approve = (toApprove, name, clean = false) =>
    run(
      () => client.post(paths.approvals, { time_card_ids: toApprove.map((card) => card.id) }),
      ({ data }) => {
        if (data.errors.length > 0) return toastError(data.errors[0]);
        if (name) return toastSuccess(`Approved ${data.approvedIds.length} ${data.approvedIds.length === 1 ? "card" : "cards"} for ${name}.`);
        if (clean) return toastSuccess(`Approved ${data.approvedIds.length} clean cards.`);
        return toastSuccess("Time card approved.");
      }
    );

  const approveClean = () => {
    if (clean.length === 0) return toastSuccess("No clean cards left. The rest have flags to review.");
    return approve(clean, null, true);
  };

  const lockPeriod = () =>
    run(
      () => client.post(paths.lock.replace(":id", timesheet.payPeriod.id)),
      () => toastSuccess("Pay period locked. Time cards can no longer be edited.")
    );

  const periodBadge = PERIOD_BADGES[timesheet.payPeriod.status];

  return (
    <>
      <div className="od-themed" style={{ padding: 24 }}>
        <PageHeader title="Staffing" supportingText={STAFFING_SUPPORTING_TEXT} />
        <div className="mt-3">
          <StaffingTabs current="timesheets" paths={tabPaths} />
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: 12 }}>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
            <div style={{ width: 320, maxWidth: "100%" }}>
              <Select
                id="timesheet-pay-period"
                options={timesheet.payPeriods.map((period) => ({ value: period.id, label: `Pay period: ${periodLabel(period.startsOn, period.endsOn)}` }))}
                value={timesheet.payPeriod.id}
                onChange={(value) => load({ pay_period_id: value }).catch(() => toastError("Could not load that pay period."))}
              />
            </div>
            {timesheet.venues.length > 1 && (
              <div style={{ width: 220 }}>
                <Select
                  id="timesheet-venue"
                  options={[{ value: ALL_VENUES, label: "All venues" }, ...timesheet.venues.map((venue) => ({ value: venue.id, label: venue.name }))]}
                  value={timesheet.venueOwnershipId ?? ALL_VENUES}
                  onChange={(value) => load({ venue_ownership_id: value }).catch(() => toastError("Could not load that venue."))}
                />
              </div>
            )}
            <Badge color={periodBadge.color}>{periodBadge.label}</Badge>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {!locked && staffChoices.people.length > 0 && (
              <Button variant="secondary" onClick={() => setEditing({ card: null })}>
                Add time card
              </Button>
            )}
            {!locked && cards.some((card) => card.canApprove) && (
              <Button variant="secondary" disabled={busy} onClick={approveClean}>
                {`Approve ${clean.length} clean cards`}
              </Button>
            )}
            {timesheet.lock.allowed && (
              <Button variant="primary" disabled={busy || Boolean(timesheet.lock.blocker)} title={timesheet.lock.blocker || "Lock period"} onClick={lockPeriod}>
                Lock period
              </Button>
            )}
            {timesheet.exportAllowed && (
              <Button variant="primary" onClick={() => setExporting(true)}>
                Export payroll
              </Button>
            )}
          </div>
        </div>

        <div className="mb-3" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
          <Metric label="Actual hours" value={hoursLabel(total("actual"))} change={{ label: `${hoursLabel(total("scheduled"))} scheduled` }} />
          <Metric label="Overtime" value={`${hoursLabel(total("overtime"))} h`} />
          {timesheet.showCost && <Metric label="Labor cost" value={moneyLabel(total("cost"))} />}
          <Metric
            label="Cards approved"
            value={`${approvedCount} / ${cards.length}`}
            chart={<ProgressBar value={cards.length ? (approvedCount / cards.length) * 100 : 0} tone="success" />}
          />
        </div>

        {needsReview > 0 && !locked && timesheet.requireDepartmentApproval && (
          <div className="mb-3">
            <Alert color="warning" description={`${needsReview} ${needsReview === 1 ? "card needs" : "cards need"} review before you can lock this period. Fix the punch or approve it as is.`} />
          </div>
        )}

        {timesheet.requireDepartmentApproval && timesheet.departments.length > 0 && (
          <div className="mb-3">
            <Card padding="md">
              <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                {timesheet.departments.map((department) => (
                  <div key={department.id} style={{ minWidth: 180, flex: "1 1 180px" }}>
                    <div className="d-flex justify-content-between mb-1" style={textStyle(13, "text-secondary", 600)}>
                      <span>{department.name}</span>
                      <span>
                        {department.approved} / {department.total} approved
                      </span>
                    </div>
                    <ProgressBar value={(department.approved / department.total) * 100} tone={department.approved === department.total ? "success" : "brand"} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        <Card padding="none">
          {timesheet.people.length === 0 ? (
            <EmptyState size="sm" showRings={false} icon={Clock} title="No time cards in this pay period" description="Cards show up here as staff clock in from the phone app or the venue tablet." />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1100 }}>
                <div style={{ display: "grid", gridTemplateColumns: PERSON_COLUMNS, gap: 8, padding: "10px 16px", ...textStyle(12, "text-tertiary", 600) }}>
                  <span style={{ paddingLeft: 24 }}>Staff member</span>
                  <span>Scheduled</span>
                  <span>Actual</span>
                  <span>Regular</span>
                  <span>OT</span>
                  <span>DT</span>
                  <span>Breaks</span>
                  <span>{timesheet.showCost ? "Cost" : ""}</span>
                  <span>Flags</span>
                  <span>Status</span>
                  <span />
                </div>
                {timesheet.people.map((person) => (
                  <PersonRow
                    key={person.id}
                    person={person}
                    expanded={Boolean(expanded[person.id])}
                    onToggle={() => setExpanded((current) => ({ ...current, [person.id]: !current[person.id] }))}
                    showCost={timesheet.showCost}
                    locked={locked}
                    busy={busy}
                    onApprove={approve}
                    onEdit={(card) => setEditing({ card })}
                  />
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
      {editing && (
        <TimeCardDrawer
          card={editing.card}
          staffChoices={staffChoices}
          paths={paths}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load().catch(() => toastError("Could not refresh the timesheet."));
          }}
        />
      )}
      {exporting && (
        <PayrollExportModal
          path={paths.payrollExport.replace(":id", timesheet.payPeriod.id)}
          onClose={() => setExporting(false)}
          onExported={() => {
            setExporting(false);
            load().catch(() => toastError("Could not reload the timesheet."));
          }}
        />
      )}
      <ToastContainer icon={false} />
    </>
  );
};

StaffingTimesheetsApp.propTypes = {
  tabPaths: staffingTabPathsShape.isRequired,
  timesheet: PropTypes.shape({
    payPeriod: PropTypes.shape({ id: PropTypes.number.isRequired, startsOn: PropTypes.string.isRequired, endsOn: PropTypes.string.isRequired, status: PropTypes.string.isRequired }).isRequired,
    payPeriods: PropTypes.array.isRequired,
    venues: PropTypes.array.isRequired,
    venueOwnershipId: PropTypes.number,
    showCost: PropTypes.bool.isRequired,
    requireDepartmentApproval: PropTypes.bool.isRequired,
    lock: PropTypes.shape({ allowed: PropTypes.bool.isRequired, blocker: PropTypes.string }).isRequired,
    exportAllowed: PropTypes.bool.isRequired,
    people: PropTypes.array.isRequired,
    departments: PropTypes.array.isRequired,
  }).isRequired,
  staffChoices: PropTypes.shape({ venues: PropTypes.array.isRequired, people: PropTypes.array.isRequired }).isRequired,
  paths: PropTypes.shape({
    timesheet: PropTypes.string.isRequired,
    timeCards: PropTypes.string.isRequired,
    timeCard: PropTypes.string.isRequired,
    approvals: PropTypes.string.isRequired,
    lock: PropTypes.string.isRequired,
    payrollExport: PropTypes.string.isRequired,
  }).isRequired,
};

export default StaffingTimesheetsApp;
